import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip, Alert, Stack, Switch, FormControlLabel
} from '@mui/material';
import { Add, Edit, Category, Inventory2, LocalOffer } from '@mui/icons-material';
import api from '../../services/api';

const PRODUCT_TYPES = ['flat_fee','bundle','per_seat','per_agent','usage'];
const DESTINATIONS = ['UK','US','UAE','KSA','Global'];
const CURRENCIES = ['AED','USD'];

const TYPE_COLOR: any = { flat_fee:'primary', bundle:'success', per_seat:'info', per_agent:'secondary', usage:'warning' };
const TYPE_LABEL: any = { flat_fee:'Flat Fee', bundle:'Bundle', per_seat:'Per Seat', per_agent:'Per Agent', usage:'Usage/Overage' };

export const ProductCataloguePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [skus, setSkus] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<{ type: string; data?: any } | null>(null);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, s] = await Promise.all([
        api.get('/subscriptions/categories'),
        api.get('/subscriptions/products'),
        api.get('/subscriptions/skus'),
      ]);
      setCategories(c.data);
      setProducts(p.data);
      setSkus(s.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = (type: string) => { setForm({}); setError(''); setDialog({ type }); };
  const openEdit   = (type: string, data: any) => { setForm({ ...data }); setError(''); setDialog({ type, data }); };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const isEdit = !!dialog?.data;
      if (dialog?.type === 'category') {
        isEdit ? await api.patch(`/subscriptions/categories/${dialog.data.id}`, form) : await api.post('/subscriptions/categories', form);
      } else if (dialog?.type === 'product') {
        isEdit ? await api.patch(`/subscriptions/products/${dialog.data.id}`, form) : await api.post('/subscriptions/products', form);
      } else if (dialog?.type === 'sku') {
        isEdit ? await api.patch(`/subscriptions/skus/${dialog.data.id}`, form) : await api.post('/subscriptions/skus', form);
      }
      setDialog(null); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <Inventory2 sx={{ color:'primary.main', fontSize:32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Product Catalogue</Typography>
            <Typography variant="body2" color="text.secondary">Manage services, bundles and pricing</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />}
          onClick={() => openCreate(['category','product','sku'][tab])}>
          Add {['Category','Product','SKU'][tab]}
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<Category />} iconPosition="start" label={`Categories (${categories.length})`} />
        <Tab icon={<Inventory2 />} iconPosition="start" label={`Products (${products.length})`} />
        <Tab icon={<LocalOffer />} iconPosition="start" label={`SKUs / Pricing (${skus.length})`} />
      </Tabs>

      {loading ? <Box sx={{ p:6, textAlign:'center' }}><CircularProgress /></Box> : (
        <Card>
          {/* ── Categories ── */}
          {tab === 0 && (
            <Table size="small">
              <TableHead><TableRow>
                {['Category','Description','Icon','Order','Active','Actions'].map(h => <TableCell key={h} sx={{ fontWeight:700 }}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {categories.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell fontWeight={600}><Typography fontWeight={600}>{r.name}</Typography></TableCell>
                    <TableCell sx={{ color:'text.secondary' }}>{r.description || '—'}</TableCell>
                    <TableCell sx={{ fontFamily:'monospace', fontSize:12 }}>{r.icon}</TableCell>
                    <TableCell>{r.sort_order}</TableCell>
                    <TableCell><Chip size="small" label={r.is_active ? 'Active' : 'Inactive'} color={r.is_active ? 'success' : 'default'} /></TableCell>
                    <TableCell><Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit('category', r)}><Edit fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* ── Products ── */}
          {tab === 1 && (
            <Table size="small">
              <TableHead><TableRow>
                {['Category','Product','Type','Active','Actions'].map(h => <TableCell key={h} sx={{ fontWeight:700 }}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {products.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ color:'text.secondary' }}>{r.category_name}</TableCell>
                    <TableCell><Typography fontWeight={600}>{r.name}</Typography></TableCell>
                    <TableCell><Chip size="small" label={TYPE_LABEL[r.product_type]} color={TYPE_COLOR[r.product_type]} /></TableCell>
                    <TableCell><Chip size="small" label={r.is_active ? 'Active' : 'Inactive'} color={r.is_active ? 'success' : 'default'} /></TableCell>
                    <TableCell><Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit('product', r)}><Edit fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* ── SKUs ── */}
          {tab === 2 && (
            <Table size="small">
              <TableHead><TableRow>
                {['Product','SKU Name','Dest.','Minutes','Price','Currency','Overage Rate','Active','Actions'].map(h => <TableCell key={h} sx={{ fontWeight:700, whiteSpace:'nowrap' }}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {skus.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.category_name}</Typography>
                    </TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.destination ? <Chip size="small" label={r.destination} /> : '—'}</TableCell>
                    <TableCell sx={{ fontFamily:'monospace' }}>{r.bundle_minutes ? r.bundle_minutes.toLocaleString() : '—'}</TableCell>
                    <TableCell sx={{ fontFamily:'monospace', fontWeight:600 }}>{parseFloat(r.default_price).toFixed(2)}</TableCell>
                    <TableCell><Chip size="small" label={r.currency} color={r.currency==='USD'?'success':'primary'} /></TableCell>
                    <TableCell sx={{ fontFamily:'monospace', fontSize:12, color:'text.secondary' }}>
                      {parseFloat(r.overage_rate) > 0 ? `${r.overage_rate}/${r.currency}` : '—'}
                    </TableCell>
                    <TableCell><Chip size="small" label={r.is_active ? 'Active' : 'Inactive'} color={r.is_active ? 'success' : 'default'} /></TableCell>
                    <TableCell><Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit('sku', r)}><Edit fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* ── Dialog ── */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog?.data ? 'Edit' : 'New'} {dialog?.type === 'category' ? 'Category' : dialog?.type === 'product' ? 'Product' : 'SKU'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt:1 }}>
            {dialog?.type === 'category' && (<>
              <TextField fullWidth size="small" label="Category Name *" value={form.name||''} onChange={e => setForm({...form, name:e.target.value})} />
              <TextField fullWidth size="small" label="Description" value={form.description||''} onChange={e => setForm({...form, description:e.target.value})} />
              <TextField fullWidth size="small" label="Icon (MUI icon name)" value={form.icon||''} onChange={e => setForm({...form, icon:e.target.value})} />
              <TextField fullWidth size="small" type="number" label="Sort Order" value={form.sort_order||0} onChange={e => setForm({...form, sort_order:parseInt(e.target.value)})} />
              {dialog.data && <FormControlLabel control={<Switch checked={form.is_active !== false} onChange={e => setForm({...form, is_active:e.target.checked})} />} label="Active" />}
            </>)}

            {dialog?.type === 'product' && (<>
              <TextField select fullWidth size="small" label="Category *" value={form.category_id||''} onChange={e => setForm({...form, category_id:e.target.value})}>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" label="Product Name *" value={form.name||''} onChange={e => setForm({...form, name:e.target.value})} />
              <TextField fullWidth size="small" label="Description" value={form.description||''} onChange={e => setForm({...form, description:e.target.value})} />
              <TextField select fullWidth size="small" label="Type *" value={form.product_type||''} onChange={e => setForm({...form, product_type:e.target.value})}>
                {PRODUCT_TYPES.map(t => <MenuItem key={t} value={t}>{TYPE_LABEL[t]}</MenuItem>)}
              </TextField>
              {dialog.data && <FormControlLabel control={<Switch checked={form.is_active !== false} onChange={e => setForm({...form, is_active:e.target.checked})} />} label="Active" />}
            </>)}

            {dialog?.type === 'sku' && (<>
              <TextField select fullWidth size="small" label="Product *" value={form.product_id||''} onChange={e => setForm({...form, product_id:e.target.value})}>
                {products.filter(p => p.is_active).map(p => <MenuItem key={p.id} value={p.id}>{p.category_name} — {p.name}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" label="SKU Name *" value={form.name||''} onChange={e => setForm({...form, name:e.target.value})} />
              <TextField select fullWidth size="small" label="Destination" value={form.destination||''} onChange={e => setForm({...form, destination:e.target.value})}>
                <MenuItem value=""><em>None</em></MenuItem>
                {DESTINATIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" type="number" label="Bundle Minutes" value={form.bundle_minutes||''} onChange={e => setForm({...form, bundle_minutes:parseInt(e.target.value)||null})} />
              <Box sx={{ display:'flex', gap:1 }}>
                <TextField fullWidth size="small" type="number" label="Default Price *" value={form.default_price||''} onChange={e => setForm({...form, default_price:parseFloat(e.target.value)||0})} />
                <TextField select size="small" label="Currency" value={form.currency||'USD'} onChange={e => setForm({...form, currency:e.target.value})} sx={{ minWidth:100 }}>
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Box>
              <TextField fullWidth size="small" type="number" label="Overage Rate (per min)" value={form.overage_rate||''} onChange={e => setForm({...form, overage_rate:parseFloat(e.target.value)||0})} inputProps={{ step:'0.001' }} />
              {dialog.data && <FormControlLabel control={<Switch checked={form.is_active !== false} onChange={e => setForm({...form, is_active:e.target.checked})} />} label="Active" />}
            </>)}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p:2 }}>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
